import { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { MEDIA_ASSET_TYPE } from "@models/data/mediaTileData";
import type {
  ISurfacePlacementModel,
  SurfacePlacementSession,
} from "./ISurfacePlacementModel";

interface TileTransform {
  readonly position: Vec3;
  readonly rotationEuler: Vec3;
}

/**
 * Port of Unity's `GameObjectContextViewModel` surface-placement flow
 * (`BeginSurfacePlacement` / `EndSurfacePlacement`). The geometry — raycast,
 * surface-normal alignment, camera suppression — lives in the runtime
 * `SurfacePlacementController`; this model owns the mode state, the initial
 * transform (for cancel), and the persistence on commit.
 */
export class SurfacePlacementModel implements ISurfacePlacementModel {
  readonly onChanged = new Emitter<void>();
  private m_session: SurfacePlacementSession | null = null;
  private m_initial: TileTransform | null = null;
  private m_preview: TileTransform | null = null;

  constructor(
    private readonly m_project: IProjectModel,
    selection: ISelectionModel,
  ) {
    // Scene change or selecting a different object aborts the mode and
    // restores the initial transform — mirrors Unity ending surface
    // placement on navigate-away / active-scene change.
    this.m_project.onActiveSceneChanged.on(() => this.cancel());
    selection.onChanged.on((change) => {
      if (this.m_session && change.primaryId !== this.m_session.tileId) {
        this.cancel();
      }
    });
  }

  get isActive(): boolean {
    return this.m_session !== null;
  }

  get session(): SurfacePlacementSession | null {
    return this.m_session;
  }

  get instructionText(): string {
    const label = this.m_session?.isButton ? "button" : "card";
    return `Click anywhere on a surface\nto place your ${label}`;
  }

  async beginForTileAsync(sceneId: string, tileId: string): Promise<void> {
    if (this.m_session) return;
    const tile = this.m_project.getMediaTile(sceneId, tileId);
    if (!tile || tile.isLocked) return;

    this.m_initial = {
      position: tile.position,
      rotationEuler: tile.rotationEuler,
    };
    this.m_preview = null;
    this.m_session = {
      sceneId,
      tileId,
      isButton: tile.asset.mediaType === MEDIA_ASSET_TYPE.ActionButton,
    };
    this.onChanged.emit();

    // Unity `DisableSelectedMediaTileBillboard`: a billboarding card keeps
    // facing the camera and would fight the surface orientation, so persist
    // billboard off before the pick begins. (Not restored on cancel — Unity
    // leaves it off too.)
    if (tile.billboard > 0.5) {
      try {
        await this.m_project.updateMediaTileAsync(
          sceneId,
          tileId,
          { billboard: 0 },
          ["MediaTile"],
        );
      } catch {
        // Non-fatal: keep placing even if the billboard write failed.
      }
    }
  }

  preview(position: Vec3, rotationEuler: Vec3): void {
    if (!this.m_session) return;
    this.m_preview = { position, rotationEuler };
    this.m_project.previewMediaTileTransform(
      this.m_session.sceneId,
      this.m_session.tileId,
      position,
      rotationEuler,
    );
  }

  /**
   * Persist the placed pose as one undo step back to where the card started:
   * Unity opens `BeginRecordValue` on `LocalPosition` / `LocalEuler` when the
   * mode begins and closes it on Done (`GameObjectContextViewModel.cs:1328-1351`).
   * The previews already moved the card in memory, so the starting pose is
   * passed rather than read from the card.
   */
  async commitAsync(): Promise<void> {
    const session = this.m_session;
    const preview = this.m_preview;
    const initial = this.m_initial;
    this.end();
    if (!session || !preview) return;
    await this.m_project.updateMediaTileWithUndoAsync(
      session.sceneId,
      session.tileId,
      { position: preview.position, rotationEuler: preview.rotationEuler },
      ["Transform"],
      {
        label: "Place card on surface",
        before: initial ? { position: initial.position, rotationEuler: initial.rotationEuler } : undefined,
      },
    );
  }

  cancel(): void {
    const session = this.m_session;
    const initial = this.m_initial;
    this.end();
    if (session && initial) {
      // Undo the in-memory drag preview — nothing was persisted during drag.
      this.m_project.previewMediaTileTransform(
        session.sceneId,
        session.tileId,
        initial.position,
        initial.rotationEuler,
      );
    }
  }

  private end(): void {
    if (!this.m_session) return;
    this.m_session = null;
    this.m_initial = null;
    this.m_preview = null;
    this.onChanged.emit();
  }
}
